import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ProductCard, SectionTitle } from "../components/shared";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";
import { Seo, breadcrumb } from "../lib/seo";

const SORTS = [
  { key: "", label: "Rilevanza" },
  { key: "recent", label: "Più recenti" },
  { key: "price_asc", label: "Prezzo ↑" },
  { key: "price_desc", label: "Prezzo ↓" },
];

export default function Catalogo() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "";
  const sort = params.get("sort") || "";
  const featured = params.get("featured") === "1";

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const p = {};
      if (search) p.search = search;
      if (category) p.category = category;
      if (sort) p.sort = sort;
      if (featured) p.featured = true;
      api.get("/products", { params: p }).then(({ data }) => { setProducts(data); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, category, sort, featured]);

  const setParam = (key, val) => { const np = new URLSearchParams(params); val ? np.set(key, val) : np.delete(key); setParams(np); };
  const onSearch = (val) => { setSearch(val); const np = new URLSearchParams(params); val ? np.set("search", val) : np.delete("search"); setParams(np, { replace: true }); };

  const activeCat = categories.find((c) => c.id === category);
  const canonical = category ? `/catalogo?category=${category}` : "/catalogo";
  const pageTitle = activeCat
    ? `${activeCat.name} Herbalife | Herbalife Rossano`
    : "Catalogo Prodotti Herbalife | Herbalife Rossano";
  const pageDesc = activeCat
    ? `${activeCat.description || `Prodotti Herbalife della categoria ${activeCat.name}`}. Acquista online a Rossano e Corigliano-Rossano.`
    : "Sfoglia tutti i prodotti Herbalife disponibili: frullati Formula 1, integratori, linea sport e cura della persona. Acquista online a Rossano e Corigliano-Rossano.";
  const crumb = breadcrumb([
    { name: "Home", path: "/" },
    { name: "Catalogo", path: "/catalogo" },
    ...(activeCat ? [{ name: activeCat.name, path: canonical }] : []),
  ]);

  return (
    <div className="mx-auto max-w-7xl px-6 py-12">
      <Seo title={pageTitle} description={pageDesc} canonical={canonical} jsonLd={crumb} />
      <SectionTitle eyebrow="Catalogo" title={activeCat ? activeCat.name : "Tutti i prodotti"} subtitle={activeCat ? activeCat.description : "Trova i prodotti Herbalife più adatti a te."} />

      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        <div className="relative max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca prodotto..." value={search} onChange={(e) => onSearch(e.target.value)} className="pl-10 rounded-full" data-testid="catalog-search" />
        </div>
        <select value={sort} onChange={(e) => setParam("sort", e.target.value)} className="h-10 rounded-full border border-input bg-background px-4 text-sm" data-testid="catalog-sort">
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      <div className="mt-5 flex gap-2 flex-wrap">
        <button onClick={() => setParam("category", "")} className={`px-4 py-2 rounded-full text-sm font-medium ${!category ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="cat-filter-all">Tutte</button>
        {categories.map((c) => (
          <button key={c.id} onClick={() => setParam("category", c.id)} className={`px-4 py-2 rounded-full text-sm font-medium ${category === c.id ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`cat-filter-${c.id}`}>{c.name}</button>
        ))}
      </div>

      {loading ? <p className="mt-16 text-center text-slate-400">Caricamento...</p>
        : products.length === 0 ? <p className="mt-16 text-center text-slate-400" data-testid="catalog-empty">Nessun prodotto trovato.</p>
        : <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="catalog-grid">{products.map((p) => <ProductCard key={p.id} product={p} />)}</div>}
    </div>
  );
}
